import React, { useMemo } from 'react';

import { PdpSummaryHero } from 'features/app-takeover/components/pdp-summary-hero';
import { useAppTakeoverPdp } from 'features/app-takeover/hooks/use-app-takeover';
import { PickerAspectSelector } from 'features/menu/components/product-detail/picker-aspect-selector';
import { ProductCombo } from 'features/menu/components/product-detail/product-combo';
import { ProductCta } from 'features/menu/components/product-detail/product-cta';
import { ProductDetailHeader } from 'features/menu/components/product-detail/product-detail-flow/product-detail-header';
import { ProductItem } from 'features/menu/components/product-detail/product-detail-flow/product-item';
import {
  ProductHomeInnerLayout,
  ProductHomeLayoutContainer,
  ProductInformationSection,
  ProductSelectionsContainer,
} from 'features/menu/components/product-detail/product-home/styled';
import { ProductInformation } from 'features/menu/components/product-detail/product-information';
import { ProductNutritionInformation } from 'features/menu/components/product-detail/product-nutrition-information/product-nutrition-information';
import { useComboNutritionItems } from 'features/menu/components/product-detail/product-nutrition-information/use-combo-nutrition-items';
import { ProductOverview } from 'features/menu/components/product-detail/product-overview';
import {
  ProductCustomizationEvents,
  transformProductWizardStepToEventAttr,
} from 'features/menu/components/product-detail/utils';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import { ProductWizardStep } from 'features/menu/components/product-wizard/types';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';
import { capitalize } from 'lodash-es';
import { ScrollView, StyleSheet } from 'react-native';

import { ProductUpsell } from 'components/product-detail-v2/product-upsell';
import FullWidthAutoHeightImage from 'components/ui/full-width-auto-height-image';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { getFirstDefaultId, getFirstOption, isCombo, isItem } from 'state/unified-menu/utils';
import { primitive } from 'styles/constants';

export const ProductHome = () => {
  const isMobile = useIsMobileBreakpoint();

  const { currentFlowStep, cartIdEditing, id } = useProductWizardFlow();
  const {
    backgroundColor: takeoverBg,
    backgroundImage: takeoverBgImage,
    summaryPageBackgroundImage: takeoverSummaryPageBgImage,
    summaryPageBackgroundImageStyle: takeoverSummaryPageBgImageStyle,
  } = useAppTakeoverPdp(id);

  const { menuObject, selectedProduct } = useProductWizardContext();
  // Combos carry allergens per slot item, so the disclaimer must merge all of
  // them instead of showing only the combo entry's own record.
  const comboNutritionItems = useComboNutritionItems();

  const selectedProductName = selectedProduct.name?.locale ?? '';
  const isPickerReview = currentFlowStep.step === ProductWizardStep.PickerReview;

  const getHeaderTitle = () => {
    if (!isPickerReview) {
      return selectedProductName;
    }

    if (isCombo(selectedProduct)) {
      return t('reviewYourMeal');
    }

    return t('reviewYourSelection');
  };

  const productOverviewTitle = isPickerReview ? selectedProductName : undefined;
  const hasSummaryHero = isPickerReview && !!takeoverSummaryPageBgImage;
  const takeoverBackgroundImage = hasSummaryHero ? undefined : takeoverBgImage;

  const showInfo = Boolean(
    currentFlowStep.step === ProductWizardStep.ProductHome ||
      currentFlowStep.step === ProductWizardStep.PickerReview ||
      cartIdEditing
  );

  const showProductUpsell =
    currentFlowStep.step === ProductWizardStep.PickerReview ||
    isCombo(menuObject) ||
    isItem(menuObject);

  const showProductInformation = currentFlowStep.step === ProductWizardStep.ProductHome;
  const productOverview = showInfo ? <ProductOverview productTitle={productOverviewTitle} /> : null;
  const productFlowTestId = isItem(menuObject)
    ? 'product-item-flow'
    : isCombo(menuObject)
    ? 'product-combo-flow'
    : 'product-picker-flow';

  const eventAttributes = useMemo<{ [key: string]: string } | undefined>(() => {
    // Picker Aspect Selection Step
    if (currentFlowStep.step === ProductWizardStep.PickerSelection) {
      return {
        name: ProductCustomizationEvents.PRODUCT_CUSTOMIZATION,
        comboSlotType: capitalize(
          transformProductWizardStepToEventAttr(ProductWizardStep.PickerSelection)
        ),
      } as Record<string, string>;
    }

    // Product Home Step
    return {
      name: transformProductWizardStepToEventAttr(currentFlowStep.step),
    } as Record<string, string>;
  }, [currentFlowStep.step]);

  return (
    <>
      <ProductDetailHeader title={getHeaderTitle()} eventAttributes={eventAttributes} />
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ flex: 1, backgroundColor: takeoverBg ?? primitive.$white }}
        contentContainerStyle={styles.scrollContent}
      >
        {takeoverBackgroundImage ? (
          <FullWidthAutoHeightImage
            source={takeoverBackgroundImage}
            style={styles.backgroundImage}
          />
        ) : null}

        {hasSummaryHero ? (
          <PdpSummaryHero
            backgroundImage={takeoverSummaryPageBgImage}
            backgroundImageStyle={takeoverSummaryPageBgImageStyle}
          >
            {productOverview}
          </PdpSummaryHero>
        ) : (
          productOverview
        )}

        <ProductSelectionsContainer testID="product-home-customization-panel">
          <ProductHomeLayoutContainer isFullContainer={isMobile}>
            <ProductHomeInnerLayout $isFullContainer={isMobile} testID={productFlowTestId}>
              <ProductHomeContent />
            </ProductHomeInnerLayout>
          </ProductHomeLayoutContainer>
        </ProductSelectionsContainer>
        {showInfo && (
          <ProductHomeLayoutContainer
            isFullContainer={isMobile}
            backgroundColor={takeoverBg ?? primitive.$mayoEggWhite}
          >
            <ProductHomeInnerLayout>
              <ProductInformationSection>
                {showProductUpsell && <ProductUpsell />}
                {showProductInformation && <ProductInformation selectedProduct={selectedProduct} />}
                <ProductNutritionInformation
                  selectedProduct={selectedProduct}
                  nutritionItems={comboNutritionItems}
                />
              </ProductInformationSection>
            </ProductHomeInnerLayout>
          </ProductHomeLayoutContainer>
        )}
      </ScrollView>
      <ProductCta />
    </>
  );
};

const styles = StyleSheet.create({
  backgroundImage: {
    position: 'absolute',
  },
  scrollContent: {
    flexGrow: 1,
  },
});

export const ProductHomeContent = () => {
  const { currentFlowStep, cartIdEditing } = useProductWizardFlow();
  const { menuObject } = useProductWizardContext();

  if (isItem(menuObject)) {
    return <ProductItem />;
  }

  if (
    isCombo(menuObject) ||
    currentFlowStep.step === ProductWizardStep.PickerReview ||
    !!cartIdEditing
  ) {
    return <ProductCombo />;
  }

  const isFirstAspect = currentFlowStep.step === ProductWizardStep.ProductHome;
  // Prefer pickerAspectKey from the wizard flow;
  // - Use getFirstDefaultId (same as buildFlow) when flow is empty.
  // - Use getFirstOption when the resulting item does not have modifiers to customize (ie. Frozen Drinks).
  const pickerAspectId =
    currentFlowStep.pickerAspectKey ||
    getFirstDefaultId(menuObject) ||
    getFirstOption(menuObject).entryId;
  return <PickerAspectSelector pickerAspectId={pickerAspectId} isFirstAspect={isFirstAspect} />;
};
